import React, { useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../../services/api/appointment.service";
import { DetailedAppointment } from "../types";

interface CancelAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: DetailedAppointment;
  reload: () => void;
}

export const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  isOpen,
  onClose,
  appointment,
  reload,
}) => {
  const [cancellationReason, setCancellationReason] = useState("");
  const [isCanceling, setIsCanceling] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!cancellationReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    const userStr = localStorage.getItem("auth_user");
    const user = JSON.parse(userStr || "{}");
    const clinicId = user.id;

    setIsCanceling(true);
    try {
      const response = await AppointmentService.cancleAppointment(
        appointment.id,
        clinicId,
        cancellationReason
      );
      if (response.success) {
        toast.success("Appointment cancelled successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to cancel appointment");
      }
    } catch (error) {
      toast.error("Failed to cancel appointment");
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden border border-gray-100">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Cancel Appointment</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-gray-100 hover:text-gray-600 p-1.5 rounded-lg transition-colors"
            disabled={isCanceling}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Reason for Cancellation <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-1 focus:ring-red-500 text-xs resize-none"
              rows={4}
              placeholder="Please enter the reason for cancelling..."
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 gap-3 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            disabled={isCanceling}
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2 bg-red-600 rounded-lg text-xs font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            disabled={isCanceling || !cancellationReason.trim()}
          >
            {isCanceling ? "Cancelling..." : "Confirm Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};
